import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardBody, ListGroup, ListGroupItem, Button, Spinner, InputGroup } from 'reactstrap';
import { getEmployeeAdmin, getCompanyAdmin, getOfferAdmin, getUsersAdmin } from '../../../../services/userservice';
import ModalDetails from './ModalDetails';

const EntityList = ({ title, type, isMobile }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [mensajeInicial, setMensajeInicial] = useState('');
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [itemsPerPage] = useState(10);

  // Informacion del modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedUserData, setSelectedUserData] = useState(null);


  // Construir URL con parámetros de búsqueda y paginación
  const buildQueryParams = (terminoBuscado = searchTerm, pageNum = currentPage) => {
    const skip = (pageNum - 1) * itemsPerPage;
    const params = new URLSearchParams();
    
    params.append('skip', skip);
    params.append('limit', itemsPerPage);
    
    if (terminoBuscado) {
      params.append('search', terminoBuscado);
    }
    
    return params.toString();
  };

  const handleUpdateSuccess = (id, updatedFields) => {
    setData(prevData => 
      prevData.map(item => 
        item.id === id 
          ? { ...item, ...updatedFields } 
          : item
      )
    );
  };

  const fetchData = async (searchValue = '', pageNum = 1) => {
    setLoading(true);
    setMensajeInicial("No se encuentran resultados para tu búsqueda");
    
    try {
      const queryParams = buildQueryParams(searchValue, pageNum);
      let response;
      switch (type) {
        case 'candidato':
          response = await getEmployeeAdmin(queryParams);   
          break;
        case 'empresa':
          response = await getCompanyAdmin(queryParams);   
          break;
        case 'oferta':
          response = await getOfferAdmin(queryParams); 
          break;
        case 'usuario':
          response = await getUsersAdmin(queryParams);
          break;
        default:
          console.log('Llamada probable a dashboard:', type);
      }

      const items = response.items || response.data || [];
      const total = response.total || 0;
      
      setData(items);
      setTotalItems(total);
      
      // Calcular total de páginas
      const pages = Math.ceil(total / itemsPerPage);
      setTotalPages(pages);

    } catch (error) {
      console.error("Error cargando " + title, error);
      setMensajeInicial("Error al cargar los datos");
      setData([]);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    setData([]);
    setMensajeInicial("Haga una búsqueda para mostrar resultados");
    setCurrentPage(1);
    setTotalItems(0);
    setTotalPages(0);
  }, [title]);

  // Cuando cambia la página, recargar datos
  useEffect(() => {
    if (searchTerm) {
      // Si hay búsqueda activa, mantener el término pero cambiar página
      fetchData(searchTerm, currentPage);
    } else if (currentPage > 1 || totalItems > 0) {
      // Si no hay búsqueda pero hay datos, recargar la página actual
      fetchData('', currentPage);
    }
  }, [currentPage]);

  // Manejar la búsqueda
  const handleSearch = () => {
    setCurrentPage(1); // Resetear a primera página
    setSearching(true);
    fetchData(searchTerm, 1);
  };

  // Manejar el enter en el input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Limpiar búsqueda
  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setData([]);
    setTotalItems(0);
    setTotalPages(0);
    setMensajeInicial("Haga una búsqueda para mostrar resultados");
  };

  // Cambiar de página
  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Generar array de números de página para mostrar
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }
    
    return pageNumbers;
  };


  // Componente de paginación
  const Pagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="d-flex justify-content-center align-items-center gap-2 p-3 border-top flex-wrap">
        <Button
          color="dark"
          size="sm"
          onClick={() => changePage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          ◀ Anterior
        </Button>

        
        <div className="d-none d-sm-flex gap-1">
          {getPageNumbers().map(pageNum => (
            <Button
              key={pageNum}
              color={currentPage === pageNum ? "dark" : "outline-secondary"}
              size="sm"
              onClick={() => changePage(pageNum)}
              style={{ minWidth: '40px' }}
            >
              {pageNum}
            </Button>
          ))}
        </div>

        <span className="d-sm-none small mx-2">Pág. {currentPage}</span>
        
        <Button
          color="dark"
          size="sm"
          onClick={() => changePage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Siguiente ▶
        </Button>
      </div>
    );
  };

  return (
    <Card className="shadow-sm border-0">
      <CardHeader className="bg-white py-3 border-bottom">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          <h3 className="h5 mb-0 fw-bold text-center text-md-start">{title}</h3>
          
          <div className="d-flex gap-2 w-100" style={ isMobile ? { maxWidth: '100%' } : { maxWidth: '70%' } }>
            <InputGroup className="flex-grow-1">
              <input
                type="text"
                className="form-control"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              {searchTerm && (
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={clearSearch}
                >
                  ⨯
                </button>
              )}
            </InputGroup>
            
            <Button
              color="dark"
              onClick={handleSearch}
              disabled={searching}
            >
              {searching ? <Spinner size="sm" /> : 'Buscar'}
            </Button>
          </div>
        </div>
        
        {searchTerm && (
          <div className="mt-2 text-end">
            <small className="text-muted">
              Buscando: "{searchTerm}"
            </small>
          </div>
        )}
      </CardHeader>
      
      <CardBody className="p-0">
        {loading ? (
          <div className="p-5 text-center">
            <Spinner color="dark" />
            <p className="mt-2 text-muted small">Cargando datos...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="p-5 text-center">
            <p className="mt-2 text-muted">{mensajeInicial}</p>
            {searchTerm && (
              <Button color="link" size="sm" onClick={clearSearch}>
                Limpiar búsqueda
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center">
              <small className="text-muted">
                Mostrando {data.length} de {totalItems} registros encontrados
              </small>
              <small className="text-muted">
                Página {currentPage} de {totalPages}
              </small>
            </div>
            
            <ListGroup flush>
              {data.map((item) => (
                <ListGroupItem key={item.id} className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between p-3 gap-3">
                  <div>
                    {type === 'candidato' && (
                      <>
                        <h5 className="mb-1 fw-semibold">{item.name} {item.lastname}</h5>
                        <p className="mb-0 text-muted small">                          
                          Numero de identificación: {item.identificationNumber}
                        </p>
                        {item.phone && (
                          <small className="text-secondary d-block">
                            Celular: {item.phone}
                          </small>
                        )}
                        {item.email && (
                          <small className="text-secondary d-block">
                            Correo electrónico: {item.email}
                          </small>
                        )}
                        {item.studyLevel && (
                          <small className="text-primary d-block">
                            Nivel de estudio: {item.studyLevel}
                          </small>
                        )}
                      </>
                    )}

                    {type === 'empresa' && (
                      <>
                        <h5 className="mb-1 fw-semibold">{item.name}</h5>
                        <p className="mb-0 text-muted small">
                          NIT: {item.nit}
                        </p>
                        {item.phone && (
                          <small className="text-secondary d-block">
                            Celular: {item.phone}
                          </small>
                        )}
                        {item.email && (
                          <small className="text-secondary d-block">
                            Correo electrónico: {item.email}
                          </small>
                        )}
                        {item.city && (
                          <small className="text-primary d-block">
                            Ciudad: {item.city}
                          </small>
                        )}
                      </>
                    )}

                    {type === 'oferta' && (
                      <>
                        <h5 className="mb-1 fw-semibold">{item.title}</h5>
                        <p className="mb-0 text-muted small">
                          Salario: ${item.salary?.toLocaleString() || item.salary} COP
                        </p>
                        {item.company_name && (
                          <small className="text-secondary d-block">
                            Empresa: {item.company_name}
                          </small>
                        )}
                        {item.modality && (
                          <small className="text-secondary d-block">
                            Modalidad: {item.modality}
                          </small>
                        )}
                        <div className="d-flex gap-2 mt-1">
                          {item.suspended !== undefined && (
                            <small className={`d-inline-flex align-items-center gap-1 px-2 py-0.5 rounded ${
                              item.suspended 
                                ? 'bg-danger bg-opacity-10 text-danger' 
                                : 'bg-success bg-opacity-10 text-success'
                            }`}>
                              {item.suspended ? 'Suspendida' : 'Activa'}
                            </small>
                          )}
                        </div>
                      </>
                    )}

                    {type === 'usuario' && (
                      <>
                        <h5 className="mb-1 fw-semibold">{item.username}</h5>
                        <p className="mb-0 text-muted small">
                          Correo electrónico: {item.email}
                        </p>
                        {item.role_id && (
                          <small className="text-secondary d-block">
                            Rol: {item.role_id === 1 ? 'Empresa' : (item.role_id === 2 ? 'Candidato' : 'No encontrado')}
                          </small>
                        )}
                      </>
                    )}
                  </div>
                  <Button 
                    color="dark" 
                    size="sm" 
                    className="w-40 w-sm-auto"
                    onClick={() => {
                      setSelectedItemId(item.id);
                      if (type === 'usuario') {
                        setSelectedUserData(item);
                      }
                      setModalOpen(true);
                    }}
                  >
                    {type === 'usuario' ? 'Editar Usuario' : 'Detalles'}
                  </Button>
                </ListGroupItem>
              ))}
            </ListGroup>
            
            <Pagination />
          </>
        )}
        <ModalDetails
          isOpen={modalOpen}
          toggle={() => {
            setModalOpen(false);
            setSelectedItemId(null);
            setSelectedUserData(null);
          }}
          itemId={selectedItemId}
          type={type}
          userData={selectedUserData}
          onSuccess={handleUpdateSuccess}
        />
      </CardBody>
    </Card>
    
  );
};

export default EntityList;